// Palworld September 2026 news cluster — update 1.1, the PlayStation 5 launch
// and Palworld Online mobile pre-registration.
//
// News and buying/planning impact only. Platform comparisons live in
// guides-mobile-2026.ts and 1.0 patch mechanics live in guides-patch.ts, so
// nothing here repeats those slugs, headings or FAQ answers.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

const TITLE = "Palworld Update 1.1 & PS5 News";

const ANSWER =
  "Palworld update 1.1 is confirmed and in development after 1.0, which Pocketpair has described as a starting point rather than a finish line: the studio says the patch will deliver a feature players have been requesting for a long time, without naming it yet. Alongside it, Palworld launched on PlayStation 5, and Palworld Online — the separately developed MMORPG adaptation — opened Android pre-registration, so the series now spans PC, Xbox, PlayStation and mobile at once.";

const STEPS: [string, string][] = [
  ["Update before you load a long-running save", "Patch releases change spawns, balance and build behaviour. Let the update finish before loading a base you care about, and keep a copy of the save folder first."],
  ["Re-check your tier list after the patch", "A balance pass moves which Pals are worth breeding. Compare your squad against the tier list rather than assuming last month's picks still hold."],
  ["Disable mods on patch day", "Mods built against the previous build are the most common cause of crashes and corrupted saves immediately after an update."],
  ["Decide which platform you actually play on", "The PlayStation 5 release adds a fourth platform. Cross-platform play depends on which versions your group owns, so agree that before anyone buys."],
  ["Pre-register mobile separately", "Palworld Online is its own game rather than a port of the survival title. Pre-registering does not transfer progress from the PC or console versions."],
];

const DATA: [string, string][] = [
  ["Update 1.1", "Confirmed in development, with Pocketpair stating that 1.0 was a starting point and that 1.1 delivers a long-requested feature not yet announced."],
  ["Feature reveal", "Deliberately withheld at the time of the tease — the studio confirmed the addition exists before naming it."],
  ["PlayStation 5", "Palworld released on PlayStation 5, announced during a State of Play broadcast."],
  ["Palworld Online", "A separately developed MMORPG adaptation, with Android pre-registration opened via Google Play in supported regions."],
  ["Mobile publishing", "Pocketpair signed a licensing deal with an external publisher for the mobile adaptation rather than building it in-house."],
  ["Legal backdrop", "Ongoing litigation involving the series continues in parallel with the releases and does not affect existing purchases."],
  ["What it means for saves", "No progress transfer has been announced between the survival game and the mobile MMORPG."],
];

const PITFALLS: [string, string][] = [
  ["Treating Palworld Online as a port", "It is a different game with different systems. Expecting your base, Pals and levels to appear there will lead to disappointment."],
  ["Assuming the 1.1 feature is confirmed content", "The studio confirmed a long-requested feature exists in the patch but has not said which one. Treat specific claims about it as speculation."],
  ["Buying a second platform for crossplay", "Check which platform combinations your group can actually play together on before spending money on a duplicate copy."],
  ["Loading a modded save straight after patching", "Update, then verify your mods have been rebuilt for the new version, then load. Doing it in the other order is how saves break."],
];

const FAQ: [string, string][] = [
  ["Is Palworld update 1.1 confirmed?", "Yes. Pocketpair has confirmed that update 1.1 is in development following 1.0 and has said it includes a feature the community has been asking for, without revealing which feature yet."],
  ["What is in Palworld update 1.1?", "The studio has confirmed a long-requested addition without naming it. Anything more specific circulating online is speculation until the publisher's own announcement lands."],
  ["Is Palworld on PlayStation 5?", "Yes. Palworld released on PlayStation 5 after being announced during a State of Play broadcast, adding a fourth platform alongside PC, Xbox and mobile."],
  ["What is Palworld Online?", "Palworld Online is a separately developed MMORPG adaptation of Palworld for mobile, published under a licensing deal rather than built in-house by Pocketpair."],
  ["Can I pre-register for Palworld Online on Android?", "Yes. Pre-registration opened through Google Play in supported regions and simply notifies you when the game becomes installable."],
  ["Does Palworld Online share progress with the PC game?", "No progress transfer has been announced. Treat it as a separate game with its own account and progression."],
  ["Will update 1.1 change the Pal tier list?", "Any balance pass can. Re-check the tier list and your breeding plans after the patch instead of assuming pre-patch picks still hold."],
];

const HUB = "/guides";
const HUB_LABEL = "Palworld guides hub";

function articles(): GuideArticle[] {
  const hubSentence = `Read this alongside the ${HUB_LABEL} at ${HUB} so the patch news lines up with the tier lists and breeding tools you actually play with.`;
  return [
    {
      title: TITLE,
      slug: "overview",
      intro: `${ANSWER} ${hubSentence}`,
      sections: [
        { heading: "Is Palworld update 1.1 confirmed?", text: ANSWER },
        { heading: "Why 1.1 matters more than 1.0 did", text: "Pocketpair has framed the 1.0 release as a starting point rather than a completed game, which puts the weight of the roadmap on the patches that follow it. That is unusual for a 1.0 launch and it is why the studio's comments about a long-requested feature in 1.1 drew so much attention." },
        { heading: "Three releases in one week", text: DATA.map(([k, v]) => `${k}: ${v}`).join(" ") },
        { heading: `Use it with the ${HUB_LABEL}`, text: `${hubSentence} Patch news is only useful once it changes a decision — which Pal to breed, which mount to ride, which platform to buy.` },
      ],
      externalLinks: pool(0),
    },
    {
      title: `${TITLE}: what to do now`,
      slug: "step-by-step",
      intro: "The order to handle a patch week in so nothing breaks and nothing is wasted.",
      sections: STEPS.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(1),
    },
    {
      title: `${TITLE}: reference table`,
      slug: "reference",
      intro: "The confirmed facts behind the September 2026 Palworld news, without the speculation.",
      sections: DATA.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${TITLE}: mistakes and fixes`,
      slug: "mistakes",
      intro: "Four ways players lose time or money around a patch and platform wave, each with the fix.",
      sections: PITFALLS.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${TITLE}: FAQ`,
      slug: "faq",
      intro: "Direct answers to what players are asking about update 1.1, the PlayStation 5 release and Palworld Online.",
      sections: FAQ.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(4),
    },
  ];
}

export const NEWS_GUIDES_1_1: Guide[] = [
  {
    slug: "palworld-update-1-1-ps5-news",
    title: `${TITLE} Guide & FAQ`,
    desc: "Pocketpair confirmed Palworld update 1.1 with a long-requested feature, Palworld launched on PlayStation 5, and Palworld Online opened Android pre-registration — what each one changes.",
    tag: "News",
    updated: "2026-09-06",
    keywords:
      "palworld update 1.1, palworld 1.1 patch notes, palworld ps5 release, palworld playstation 5, palworld online pre registration, palworld mobile mmorpg, palworld news",
    articles: articles(),
  },
];
